/**
 * TestcaseAnalytics Component
 * 
 * 저장된 테스트케이스를 분석하여 서비스의 취약 영역을 시각화하는 대시보드
 */

import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../auth';
import { useLanguage } from '../i18n';
import { useBodyScrollLock } from '../hooks';
import { getSavedTestCases, getTestCaseCategories, getTestcaseFailureAnalytics } from '../api';
import type { SavedTestCaseResponse, TestCaseCategory, FailureAnalyticsResponse } from '../api';

// 연결 맵(마인드맵) 기능 플래그 - 현재 숨김 처리
const ENABLE_CONNECTION_MAP = false;

interface TestcaseAnalyticsProps {
  isOpen: boolean;
  onClose: () => void;
}

interface AnalyticsData {
  totalCount: number;
  byCategory: { name: string; count: number; color: string }[];
  byFeatureTag: { name: string; count: number }[];
  byPriority: { high: number; medium: number; low: number };
  byTestCategory: { core: number; edge: number };
  recentTestcases: SavedTestCaseResponse[];
}

export default function TestcaseAnalytics({ isOpen, onClose }: TestcaseAnalyticsProps) {
  const { user } = useAuth();
  const { lang } = useLanguage();
  const [testcases, setTestcases] = useState<SavedTestCaseResponse[]>([]);
  const [categories, setCategories] = useState<TestCaseCategory[]>([]);
  const [failureData, setFailureData] = useState<FailureAnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'heatmap' | 'mindmap'>('overview');

  // 데이터 로드
  useEffect(() => {
    if (isOpen && user) {
      setIsLoading(true);
      Promise.all([
        getSavedTestCases(),
        getTestCaseCategories(),
        getTestcaseFailureAnalytics()
      ])
        .then(([tc, cat, failures]) => {
          setTestcases(tc);
          setCategories(cat);
          setFailureData(failures);
        })
        .catch(console.error)
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, user]);

  // 연결 맵 탭이 숨김 상태인데도 선택돼 있으면 강제로 다른 탭으로 이동
  useEffect(() => {
    if (!ENABLE_CONNECTION_MAP && activeTab === 'mindmap') {
      setActiveTab('overview');
    }
  }, [activeTab]);

  // 분석 데이터 계산
  const analytics: AnalyticsData = useMemo(() => {
    const byCategory: Record<string, number> = {};
    const byFeatureTag: Record<string, number> = {};
    const byPriority = { high: 0, medium: 0, low: 0 };
    const byTestCategory = { core: 0, edge: 0 };

    testcases.forEach(tc => {
      // 카테고리별
      const catName = tc.category_name || (lang === 'ko' ? '미분류' : 'Uncategorized');
      byCategory[catName] = (byCategory[catName] || 0) + 1;

      // 기능 태그별
      const tagName = tc.feature_name || (lang === 'ko' ? '태그 없음' : 'No Tag');
      byFeatureTag[tagName] = (byFeatureTag[tagName] || 0) + 1;

      // 우선순위별
      if (tc.priority === 'high') byPriority.high++;
      else if (tc.priority === 'medium') byPriority.medium++;
      else byPriority.low++;

      // 테스트 카테고리별 (core/edge)
      if (tc.test_category === 'core') byTestCategory.core++;
      else byTestCategory.edge++;
    });

    return {
      totalCount: testcases.length,
      byCategory: Object.entries(byCategory)
        .map(([name, count]) => {
          const cat = categories.find(c => c.name === name);
          return { name, count, color: cat?.color || '#6b7280' };
        })
        .sort((a, b) => b.count - a.count),
      byFeatureTag: Object.entries(byFeatureTag)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count),
      byPriority,
      byTestCategory,
      recentTestcases: testcases.slice(-5).reverse(),
    };
  }, [testcases, categories, lang]);

  // 모달 열릴 때 배경 스크롤 방지
  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const totalPriority = analytics.byPriority.high + analytics.byPriority.medium + analytics.byPriority.low;
  const totalTestCat = analytics.byTestCategory.core + analytics.byTestCategory.edge;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-[#1a1a2e] border border-terminal-border rounded-xl w-full max-w-4xl mx-4 shadow-2xl h-[85vh] max-h-[900px] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="p-6 border-b border-terminal-border bg-gradient-to-r from-purple-500/10 to-cyan-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/30 to-cyan-500/30 flex items-center justify-center">
                <span className="text-2xl">🔬</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  {lang === 'ko' ? '테스트케이스 분석' : 'Test Case Analytics'}
                </h2>
                <p className="text-sm text-terminal-dim">
                  {lang === 'ko' ? '서비스 취약 영역 파악 및 테스트 커버리지 분석' : 'Identify vulnerable areas and analyze test coverage'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-terminal-dim hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* 탭 */}
          <div className="flex gap-2 mt-4 flex-wrap">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                activeTab === 'overview'
                  ? 'bg-purple-500/30 border border-purple-500 text-purple-400'
                  : 'bg-terminal-input border border-terminal-border text-terminal-muted hover:text-terminal-text'
              }`}
            >
              <span>📊</span>
              {lang === 'ko' ? '전체 현황' : 'Overview'}
            </button>
            
            {/* 실패 분석 탭 */}
            <button
              onClick={() => setActiveTab('heatmap')}
              className={`px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                activeTab === 'heatmap'
                  ? 'bg-red-500/30 border border-red-500 text-red-400'
                  : 'bg-terminal-input border border-terminal-border text-terminal-muted hover:text-terminal-text'
              }`}
            >
              <span>🔥</span>
              {lang === 'ko' ? '실패 분석' : 'Failure Analysis'}
              {/* 조건 충족 시에만 실패 개수 배지 표시 (평가 5건 이상 AND 테스트케이스 5건 이상) */}
              {failureData && failureData.total_failures > 0 && 
               (failureData.total_evaluations >= 5 && testcases.length >= 5) && (
                <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                  {failureData.total_failures}
                </span>
              )}
            </button>
            
            {/* 연결 맵 탭 */}
            {ENABLE_CONNECTION_MAP && (
              <button
                onClick={() => setActiveTab('mindmap')}
                className={`px-4 py-2 text-sm rounded-lg transition-colors flex items-center gap-2 ${
                  activeTab === 'mindmap'
                    ? 'bg-cyan-500/30 border border-cyan-500 text-cyan-400'
                    : 'bg-terminal-input border border-terminal-border text-terminal-muted hover:text-terminal-text'
                }`}
              >
                <span>🕸️</span>
                {lang === 'ko' ? '연결 맵' : 'Connection Map'}
              </button>
            )}
          </div>
        </div>

        {/* 콘텐츠 */}
        <div className="flex-1 overflow-y-auto p-6 min-h-[500px]">
          {isLoading ? (
            <div className="flex items-center justify-center min-h-[450px] text-terminal-dim">
              <span className="animate-spin mr-2">⏳</span>
              {lang === 'ko' ? '분석 중...' : 'Analyzing...'}
            </div>
          ) : activeTab === 'heatmap' ? (
            /* 실패 분석 탭 */
            <AdvancedTabWrapper 
              evaluationCount={failureData?.total_evaluations || 0} 
              testcaseCount={testcases.length} 
              lang={lang}
              tabName={lang === 'ko' ? '실패 분석' : 'Failure Analysis'}
            >
              <FailureAnalysisView data={failureData} lang={lang} />
            </AdvancedTabWrapper>
          ) : ENABLE_CONNECTION_MAP && activeTab === 'mindmap' ? (
            /* 연결 맵 탭 */
            <AdvancedTabWrapper 
              evaluationCount={failureData?.total_evaluations || 0} 
              testcaseCount={testcases.length} 
              lang={lang}
              tabName={lang === 'ko' ? '연결 맵' : 'Connection Map'}
            >
              <ConnectionMapView testcases={testcases} lang={lang} />
            </AdvancedTabWrapper>
          ) : testcases.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[450px] text-terminal-dim">
              <span className="text-4xl mb-4">📭</span>
              <p>{lang === 'ko' ? '저장된 테스트케이스가 없습니다' : 'No saved test cases'}</p>
              <p className="text-sm mt-2 text-terminal-muted">
                {lang === 'ko' ? '테스트케이스를 생성하고 저장하면 분석 결과를 볼 수 있습니다' : 'Generate and save test cases to see analytics'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* 요약 카드들 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <SummaryCard
                  icon="📋"
                  label={lang === 'ko' ? '전체 테스트케이스' : 'Total Test Cases'}
                  value={analytics.totalCount}
                  color="purple"
                />
                <SummaryCard
                  icon="🔥"
                  label={lang === 'ko' ? '높은 우선순위' : 'High Priority'}
                  value={analytics.byPriority.high}
                  subtext={totalPriority > 0 ? `${Math.round((analytics.byPriority.high / totalPriority) * 100)}%` : '0%'}
                  color="red"
                />
                <SummaryCard
                  icon="🎯"
                  label={lang === 'ko' ? '핵심 시나리오' : 'Core Scenarios'}
                  value={analytics.byTestCategory.core}
                  subtext={totalTestCat > 0 ? `${Math.round((analytics.byTestCategory.core / totalTestCat) * 100)}%` : '0%'}
                  color="green"
                />
                <SummaryCard
                  icon="⚡"
                  label={lang === 'ko' ? '엣지 케이스' : 'Edge Cases'}
                  value={analytics.byTestCategory.edge}
                  subtext={totalTestCat > 0 ? `${Math.round((analytics.byTestCategory.edge / totalTestCat) * 100)}%` : '0%'}
                  color="yellow"
                />
              </div>

              {/* 취약 영역 분석 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* 기능 태그별 분포 */}
                <div className="bg-terminal-input rounded-xl p-4 border border-terminal-border">
                  <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                    <span>🏷️</span>
                    {lang === 'ko' ? '기능별 테스트 분포' : 'Test Distribution by Feature'}
                  </h3>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {analytics.byFeatureTag.map(item => {
                      const percent = (item.count / analytics.totalCount) * 100;
                      const isLow = item.count < 3;
                      return (
                        <div key={item.name} className="group">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className={`truncate ${isLow ? 'text-status-warning' : 'text-terminal-text'}`}>
                              {isLow && <span className="mr-1">⚠️</span>}
                              {item.name}
                            </span>
                            <span className="text-terminal-dim ml-2">{item.count}</span>
                          </div>
                          <div className="h-2 bg-terminal-bg rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                isLow ? 'bg-status-warning' : 'bg-cyan-500'
                              }`}
                              style={{ width: `${Math.max(percent, 5)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {analytics.byFeatureTag.filter(i => i.count < 3).length > 0 && (
                    <p className="text-xs text-status-warning mt-3 flex items-center gap-1">
                      <span>⚠️</span>
                      {lang === 'ko' 
                        ? `${analytics.byFeatureTag.filter(i => i.count < 3).length}개 기능의 테스트가 부족합니다`
                        : `${analytics.byFeatureTag.filter(i => i.count < 3).length} features need more tests`
                      }
                    </p>
                  )}
                </div>

                {/* 카테고리별 분포 */}
                <div className="bg-terminal-input rounded-xl p-4 border border-terminal-border">
                  <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                    <span>📁</span>
                    {lang === 'ko' ? '카테고리별 분포' : 'Distribution by Category'}
                  </h3>
                  <div className="space-y-3 max-h-48 overflow-y-auto">
                    {analytics.byCategory.map(item => {
                      const percent = (item.count / analytics.totalCount) * 100;
                      return (
                        <div key={item.name}>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-terminal-text truncate">{item.name}</span>
                            <span className="text-terminal-dim ml-2">{item.count}</span>
                          </div>
                          <div className="h-2 bg-terminal-bg rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all bg-purple-500"
                              style={{ width: `${Math.max(percent, 5)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 우선순위 분포 시각화 */}
              <div className="bg-terminal-input rounded-xl p-4 border border-terminal-border">
                <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                  <span>📊</span>
                  {lang === 'ko' ? '우선순위 분포' : 'Priority Distribution'}
                </h3>
                <div className="flex items-center gap-2 h-8">
                  {totalPriority > 0 && (
                    <>
                      <div 
                        className="h-full bg-status-danger rounded-l-lg flex items-center justify-center text-xs text-white font-medium transition-all"
                        style={{ width: `${(analytics.byPriority.high / totalPriority) * 100}%`, minWidth: analytics.byPriority.high > 0 ? '60px' : '0' }}
                      >
                        {analytics.byPriority.high > 0 && `🔥 ${analytics.byPriority.high}`}
                      </div>
                      <div 
                        className="h-full bg-status-warning flex items-center justify-center text-xs text-white font-medium transition-all"
                        style={{ width: `${(analytics.byPriority.medium / totalPriority) * 100}%`, minWidth: analytics.byPriority.medium > 0 ? '60px' : '0' }}
                      >
                        {analytics.byPriority.medium > 0 && `⚡ ${analytics.byPriority.medium}`}
                      </div>
                      <div 
                        className="h-full bg-terminal-muted rounded-r-lg flex items-center justify-center text-xs text-white font-medium transition-all"
                        style={{ width: `${(analytics.byPriority.low / totalPriority) * 100}%`, minWidth: analytics.byPriority.low > 0 ? '60px' : '0' }}
                      >
                        {analytics.byPriority.low > 0 && `💤 ${analytics.byPriority.low}`}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex items-center justify-center gap-6 mt-3 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-status-danger"></span>
                    <span className="text-terminal-dim">{lang === 'ko' ? '높음' : 'High'} ({analytics.byPriority.high})</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-status-warning"></span>
                    <span className="text-terminal-dim">{lang === 'ko' ? '중간' : 'Medium'} ({analytics.byPriority.medium})</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded bg-terminal-muted"></span>
                    <span className="text-terminal-dim">{lang === 'ko' ? '낮음' : 'Low'} ({analytics.byPriority.low})</span>
                  </span>
                </div>
              </div>

              {/* 취약 영역 경고 */}
              {analytics.byPriority.high > analytics.byPriority.low * 2 && (
                <div className="bg-status-danger/10 border border-status-danger/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🚨</span>
                    <div>
                      <h4 className="text-status-danger font-bold text-sm">
                        {lang === 'ko' ? '높은 우선순위 테스트 집중' : 'High Priority Test Concentration'}
                      </h4>
                      <p className="text-terminal-dim text-xs mt-1">
                        {lang === 'ko' 
                          ? '높은 우선순위 테스트가 낮은 우선순위보다 2배 이상 많습니다. 서비스의 핵심 기능에 취약점이 집중되어 있을 수 있습니다.'
                          : 'High priority tests outnumber low priority by more than 2x. Vulnerabilities may be concentrated in core features.'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 최근 저장된 테스트케이스 */}
              <div className="bg-terminal-input rounded-xl p-4 border border-terminal-border">
                <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                  <span>🕐</span>
                  {lang === 'ko' ? '최근 저장된 테스트케이스' : 'Recently Saved Test Cases'}
                </h3>
                <div className="space-y-2">
                  {analytics.recentTestcases.map(tc => (
                    <div key={tc.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        tc.test_category === 'core' ? 'bg-status-go/20 text-status-go' : 'bg-status-warning/20 text-status-warning'
                      }`}>
                        {tc.test_category}
                      </span>
                      <span className={`text-xs ${
                        tc.priority === 'high' ? 'text-status-danger' : 
                        tc.priority === 'medium' ? 'text-status-warning' : 'text-terminal-muted'
                      }`}>
                        {tc.priority === 'high' ? '🔥' : tc.priority === 'medium' ? '⚡' : '💤'}
                      </span>
                      <span className="text-sm text-terminal-text truncate flex-1">{tc.title}</span>
                      {tc.feature_name && (
                        <span className="text-xs text-cyan-400 flex items-center gap-1">
                          <span>🏷️</span>
                          <span>{tc.feature_name}</span>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 요약 카드 컴포넌트
function SummaryCard({ 
  icon, 
  label, 
  value, 
  subtext,
  color 
}: { 
  icon: string; 
  label: string; 
  value: number; 
  subtext?: string;
  color: 'purple' | 'red' | 'green' | 'yellow';
}) {
  const colorClasses = {
    purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
    red: 'from-red-500/20 to-red-600/10 border-red-500/30',
    green: 'from-green-500/20 to-green-600/10 border-green-500/30',
    yellow: 'from-yellow-500/20 to-yellow-600/10 border-yellow-500/30',
  };

  return (
    <div className={`bg-gradient-to-br ${colorClasses[color]} rounded-xl p-4 border`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{icon}</span>
        <span className="text-xs text-terminal-dim">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-white">{value}</span>
        {subtext && <span className="text-xs text-terminal-dim">{subtext}</span>}
      </div>
    </div>
  );
}

// 고급 탭 래퍼 (조건 미충족 시 안내 메시지 표시)
function AdvancedTabWrapper({ 
  evaluationCount, 
  testcaseCount, 
  lang, 
  tabName,
  children 
}: { 
  evaluationCount: number; 
  testcaseCount: number; 
  lang: 'ko' | 'en';
  tabName: string;
  children: React.ReactNode;
}) {
  const MIN_EVALUATIONS = 5;
  const MIN_TESTCASES = 5;
  const isEnabled = evaluationCount >= MIN_EVALUATIONS && testcaseCount >= MIN_TESTCASES;
  
  if (isEnabled) {
    return <>{children}</>;
  }
  
  return (
    <div className="flex flex-col items-center justify-center min-h-[450px]">
      <span className="text-6xl mb-4">🔒</span>
      <p className="text-terminal-dim font-medium text-lg">
        {lang === 'ko' ? `${tabName} 기능이 잠겨 있습니다` : `${tabName} is locked`}
      </p>
      <p className="text-sm mt-2 text-terminal-muted text-center max-w-md">
        {lang === 'ko' 
          ? '더 많은 데이터가 쌓이면 이 기능을 사용할 수 있습니다'
          : 'This feature will be available when more data is collected'
        }
      </p>
      <div className="mt-6 space-y-2">
        <div className={`flex items-center gap-3 text-sm ${evaluationCount >= MIN_EVALUATIONS ? 'text-status-go' : 'text-terminal-muted'}`}>
          <span className="w-6 text-center">{evaluationCount >= MIN_EVALUATIONS ? '✅' : '❌'}</span>
          <span>
            {lang === 'ko' 
              ? `평가 ${MIN_EVALUATIONS}건 이상 필요` 
              : `Need ${MIN_EVALUATIONS}+ evaluations`
            }
          </span>
          <span className="text-terminal-dim">({evaluationCount}/{MIN_EVALUATIONS})</span>
        </div>
        <div className={`flex items-center gap-3 text-sm ${testcaseCount >= MIN_TESTCASES ? 'text-status-go' : 'text-terminal-muted'}`}>
          <span className="w-6 text-center">{testcaseCount >= MIN_TESTCASES ? '✅' : '❌'}</span>
          <span>
            {lang === 'ko' 
              ? `저장된 테스트케이스 ${MIN_TESTCASES}건 이상 필요` 
              : `Need ${MIN_TESTCASES}+ saved test cases`
            }
          </span>
          <span className="text-terminal-dim">({testcaseCount}/{MIN_TESTCASES})</span>
        </div>
      </div>
    </div>
  );
}

// 실패 분석 컴포넌트 (테이블 기반)
function FailureAnalysisView({ data, lang }: { data: FailureAnalyticsResponse | null; lang: 'ko' | 'en' }) {
  const [expandedFeature, setExpandedFeature] = useState<string | null>(null);

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[450px] text-terminal-dim">
        <span className="animate-spin text-2xl mb-4">⏳</span>
        <p>{lang === 'ko' ? '데이터를 불러오는 중...' : 'Loading data...'}</p>
      </div>
    );
  }

  if (data.total_failures === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[450px]">
        <span className="text-6xl mb-4">✅</span>
        <p className="text-status-go font-bold text-lg">{lang === 'ko' ? '모든 테스트 통과!' : 'All Tests Passed!'}</p>
        <p className="text-sm mt-2 text-terminal-muted">
          {lang === 'ko' ? '실패한 테스트 케이스가 없습니다' : 'No failed test cases found'}
        </p>
      </div>
    );
  }

  const maxFailures = Math.max(...data.by_feature.map(f => f.failure_count), 1);

  return (
    <div className="space-y-6">
      {/* 요약 바 */}
      <div className="bg-terminal-input rounded-xl p-4 border border-terminal-border">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-terminal-text">{data.total_testcases}</p>
              <p className="text-xs text-terminal-dim">{lang === 'ko' ? '전체' : 'Total'}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-400">{data.total_failures}</p>
              <p className="text-xs text-terminal-dim">{lang === 'ko' ? '실패' : 'Failures'}</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-orange-400">{data.failure_rate}%</p>
              <p className="text-xs text-terminal-dim">{lang === 'ko' ? '실패율' : 'Failure Rate'}</p>
            </div>
          </div>
          
          {/* 우선순위 요약 */}
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-sm">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              <span className="text-red-400 font-medium">{data.by_priority.high}</span>
            </span>
            <span className="flex items-center gap-1 text-sm">
              <span className="w-3 h-3 rounded-full bg-orange-500"></span>
              <span className="text-orange-400 font-medium">{data.by_priority.medium}</span>
            </span>
            <span className="flex items-center gap-1 text-sm">
              <span className="w-3 h-3 rounded-full bg-gray-500"></span>
              <span className="text-gray-400 font-medium">{data.by_priority.low}</span>
            </span>
          </div>
        </div>
      </div>

      {/* 기능별 실패 테이블 */}
      <div className="bg-terminal-input rounded-xl border border-terminal-border overflow-hidden">
        <div className="p-4 border-b border-terminal-border">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <span>📊</span>
            {lang === 'ko' ? '기능별 실패 분포' : 'Failures by Feature'}
          </h3>
        </div>
        
        <div className="divide-y divide-terminal-border">
          {data.by_feature.map((feature, idx) => {
            const percent = (feature.failure_count / maxFailures) * 100;
            const isExpanded = expandedFeature === feature.feature_name;
            
            return (
              <div key={idx}>
                <button
                  onClick={() => setExpandedFeature(isExpanded ? null : feature.feature_name)}
                  className="w-full p-4 flex items-center gap-4 hover:bg-white/5 transition-colors text-left"
                >
                  {/* 순위 */}
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    idx === 0 ? 'bg-red-500 text-white' :
                    idx === 1 ? 'bg-orange-500 text-white' :
                    idx === 2 ? 'bg-yellow-500 text-black' :
                    'bg-terminal-border text-terminal-dim'
                  }`}>
                    {idx + 1}
                  </span>
                  
                  {/* 기능명 */}
                  <span className="flex-1 text-sm text-white font-medium truncate">
                    {feature.feature_name}
                  </span>
                  
                  {/* 실패 바 */}
                  <div className="w-32 h-2 bg-terminal-bg rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        percent >= 80 ? 'bg-red-500' : percent >= 50 ? 'bg-orange-500' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  
                  {/* 실패 수 */}
                  <span className={`text-lg font-bold w-8 text-right ${
                    percent >= 80 ? 'text-red-400' : percent >= 50 ? 'text-orange-400' : 'text-yellow-400'
                  }`}>
                    {feature.failure_count}
                  </span>
                  
                  {/* 확장 아이콘 */}
                  <svg 
                    className={`w-4 h-4 text-terminal-dim transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {/* 확장된 상세 정보 */}
                {isExpanded && (
                  <div className="px-4 pb-4 bg-terminal-bg/50">
                    <p className="text-xs text-terminal-dim mb-2 pl-10">
                      {lang === 'ko' ? '최근 실패 케이스:' : 'Recent failures:'}
                    </p>
                    <div className="space-y-2 pl-10">
                      {feature.recent_failures.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <span className={`w-2 h-2 rounded-full ${
                            f.priority === 'high' ? 'bg-red-500' : 
                            f.priority === 'medium' ? 'bg-orange-500' : 'bg-gray-500'
                          }`}></span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            f.category === 'core' ? 'bg-status-go/20 text-status-go' : 'bg-status-warning/20 text-status-warning'
                          }`}>
                            {f.category}
                          </span>
                          <span className="text-terminal-text">{f.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* 최근 실패 타임라인 */}
      <div className="bg-terminal-input rounded-xl p-4 border border-terminal-border">
        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
          <span>🕐</span>
          {lang === 'ko' ? '최근 실패 타임라인' : 'Recent Failure Timeline'}
        </h3>
        <div className="space-y-4 max-h-64 overflow-y-auto">
          {(() => {
            // 같은 일시별로 그룹화
            const groups: { date: string; failures: typeof data.recent_failures }[] = [];
            data.recent_failures.slice(0, 20).forEach(failure => {
              const dateStr = failure.evaluated_at 
                ? new Date(failure.evaluated_at).toLocaleString() 
                : '';
              const existing = groups.find(g => g.date === dateStr);
              if (existing) {
                existing.failures.push(failure);
              } else {
                groups.push({ date: dateStr, failures: [failure] });
              }
            });
            
            return groups.slice(0, 10).map((group, idx, arr) => {
              // 가장 높은 우선순위 색상 결정
              const hasHigh = group.failures.some(f => f.priority === 'high');
              const hasMedium = group.failures.some(f => f.priority === 'medium');
              const dotColor = hasHigh ? 'bg-red-500' : hasMedium ? 'bg-orange-500' : 'bg-gray-500';
              const showConnector = idx < arr.length - 1;
              
              return (
                <div key={idx} className="flex items-start gap-3">
                  {/* 타임라인 도트 */}
                  <div className="flex flex-col items-center">
                    <span className={`w-3 h-3 rounded-full ${dotColor}`}></span>
                    {showConnector && (
                      <div className="w-0.5 flex-1 min-h-[20px] bg-terminal-border mt-1"></div>
                    )}
                  </div>
                  
                  {/* 내용 */}
                  <div className="flex-1 -mt-0.5">
                    <p className="text-[10px] text-terminal-muted mb-1">{group.date}</p>
                    <div className="space-y-2">
                      {group.failures.map((failure, fIdx) => (
                        <div key={fIdx} className="pl-2 border-l-2 border-terminal-border">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`w-2 h-2 rounded-full ${
                              failure.priority === 'high' ? 'bg-red-500' : 
                              failure.priority === 'medium' ? 'bg-orange-500' : 'bg-gray-500'
                            }`}></span>
                            <span className="text-sm text-white font-medium">{failure.title}</span>
                            <span className="text-xs text-cyan-400">🏷️ {failure.feature_name}</span>
                          </div>
                          {failure.memo && (
                            <p className="text-xs text-terminal-dim mt-1 ml-4">💬 {failure.memo}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
}

// 연결 맵 (마인드맵) 컴포넌트
function ConnectionMapView({ testcases, lang }: { testcases: SavedTestCaseResponse[]; lang: 'ko' | 'en' }) {
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  
  if (testcases.length < 3) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[450px]">
        <span className="text-6xl mb-4">🕸️</span>
        <p className="text-terminal-dim font-medium">
          {lang === 'ko' ? '연결 맵을 생성하려면 최소 3개의 테스트케이스가 필요합니다' : 'Need at least 3 test cases to generate connection map'}
        </p>
        <p className="text-sm mt-2 text-terminal-muted">
          {lang === 'ko' ? `현재 ${testcases.length}개 저장됨` : `Currently ${testcases.length} saved`}
        </p>
      </div>
    );
  }

  // 기능 태그별로 그룹화
  const byFeature: Record<string, SavedTestCaseResponse[]> = {};
  testcases.forEach(tc => {
    const tag = tc.feature_name || (lang === 'ko' ? '미분류' : 'Uncategorized');
    if (!byFeature[tag]) byFeature[tag] = [];
    byFeature[tag].push(tc);
  });

  const features = Object.keys(byFeature);
  const centerX = 350;
  const centerY = 250;
  const radius = 180;

  // 노드 위치 계산
  const featureNodes = features.map((feature, idx) => {
    const angle = (idx / features.length) * 2 * Math.PI - Math.PI / 2;
    return {
      id: feature,
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
      count: byFeature[feature].length,
      testcases: byFeature[feature],
    };
  });

  // 연결선 계산 (같은 카테고리 또는 우선순위를 가진 기능들 연결)
  const connections: { from: string; to: string; type: string }[] = [];
  for (let i = 0; i < features.length; i++) {
    for (let j = i + 1; j < features.length; j++) {
      const tc1 = byFeature[features[i]];
      const tc2 = byFeature[features[j]];
      
      // 같은 카테고리 공유 시 연결
      const shareCategory = tc1.some(t1 => tc2.some(t2 => t1.test_category === t2.test_category));
      // 같은 우선순위 공유 시 연결
      const sharePriority = tc1.some(t1 => tc2.some(t2 => t1.priority === t2.priority));
      
      if (shareCategory || sharePriority) {
        connections.push({
          from: features[i],
          to: features[j],
          type: shareCategory && sharePriority ? 'strong' : 'weak'
        });
      }
    }
  }

  const getNodeById = (id: string) => featureNodes.find(n => n.id === id);

  return (
    <div className="space-y-4">
      {/* 범례 */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-4 text-xs">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-purple-500"></span>
            <span className="text-terminal-dim">{lang === 'ko' ? '기능 그룹' : 'Feature Group'}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-6 h-0.5 bg-cyan-400"></span>
            <span className="text-terminal-dim">{lang === 'ko' ? '강한 연결' : 'Strong'}</span>
          </span>
          <span className="flex items-center gap-1">
            <span className="w-6 h-0.5 bg-terminal-border"></span>
            <span className="text-terminal-dim">{lang === 'ko' ? '약한 연결' : 'Weak'}</span>
          </span>
        </div>
        <p className="text-xs text-terminal-dim">
          {lang === 'ko' ? '노드를 클릭하여 상세 정보 확인' : 'Click node for details'}
        </p>
      </div>

      {/* SVG 맵 */}
      <div className="bg-terminal-input rounded-xl border border-terminal-border p-4 overflow-auto">
        <svg width="700" height="500" className="mx-auto">
          {/* 배경 그라데이션 */}
          <defs>
            <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx={centerX} cy={centerY} r={radius + 60} fill="url(#centerGlow)" />
          
          {/* 연결선 */}
          {connections.map((conn, idx) => {
            const fromNode = getNodeById(conn.from);
            const toNode = getNodeById(conn.to);
            if (!fromNode || !toNode) return null;
            
            return (
              <line
                key={idx}
                x1={fromNode.x}
                y1={fromNode.y}
                x2={toNode.x}
                y2={toNode.y}
                stroke={conn.type === 'strong' ? '#22d3ee' : '#374151'}
                strokeWidth={conn.type === 'strong' ? 2 : 1}
                strokeOpacity={conn.type === 'strong' ? 0.6 : 0.3}
              />
            );
          })}
          
          {/* 중앙 노드 */}
          <g>
            <circle cx={centerX} cy={centerY} r={40} fill="#8b5cf6" fillOpacity="0.2" stroke="#8b5cf6" strokeWidth="2" />
            <text x={centerX} y={centerY - 5} textAnchor="middle" fill="white" fontSize="20">🧪</text>
            <text x={centerX} y={centerY + 15} textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">
              {testcases.length}
            </text>
          </g>
          
          {/* 기능 노드들 */}
          {featureNodes.map((node, idx) => {
            const isSelected = selectedNode === node.id;
            const size = Math.min(30 + node.count * 3, 50);
            
            return (
              <g 
                key={idx} 
                onClick={() => setSelectedNode(isSelected ? null : node.id)}
                className="cursor-pointer"
              >
                {/* 연결선 to 센터 */}
                <line
                  x1={centerX}
                  y1={centerY}
                  x2={node.x}
                  y2={node.y}
                  stroke="#8b5cf6"
                  strokeWidth="1"
                  strokeOpacity="0.3"
                  strokeDasharray="4 4"
                />
                
                {/* 노드 */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={size}
                  fill={isSelected ? '#8b5cf6' : '#1e293b'}
                  stroke={isSelected ? '#a78bfa' : '#6366f1'}
                  strokeWidth={isSelected ? 3 : 2}
                  className="transition-all hover:stroke-purple-400"
                />
                
                {/* 카운트 */}
                <text
                  x={node.x}
                  y={node.y + 4}
                  textAnchor="middle"
                  fill="white"
                  fontSize="14"
                  fontWeight="bold"
                >
                  {node.count}
                </text>
                
                {/* 라벨 */}
                <text
                  x={node.x}
                  y={node.y + size + 16}
                  textAnchor="middle"
                  fill={isSelected ? '#a78bfa' : '#9ca3af'}
                  fontSize="11"
                  fontWeight="medium"
                >
                  {node.id.length > 10 ? node.id.slice(0, 10) + '...' : node.id}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* 선택된 노드 상세 정보 */}
      {selectedNode && byFeature[selectedNode] && (
        <div className="bg-terminal-input rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-purple-400 flex items-center gap-2">
              <span>🏷️</span>
              {selectedNode}
            </h4>
            <span className="text-xs text-terminal-dim">
              {byFeature[selectedNode].length} {lang === 'ko' ? '개 테스트케이스' : 'test cases'}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
            {byFeature[selectedNode].map((tc, idx) => (
              <div key={idx} className="p-2 bg-terminal-bg rounded-lg text-sm flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${
                  tc.priority === 'high' ? 'bg-red-500' : 
                  tc.priority === 'medium' ? 'bg-orange-500' : 'bg-gray-500'
                }`}></span>
                <span className={`text-[10px] px-1 py-0.5 rounded ${
                  tc.test_category === 'core' ? 'bg-status-go/20 text-status-go' : 'bg-status-warning/20 text-status-warning'
                }`}>
                  {tc.test_category}
                </span>
                <span className="text-terminal-text truncate">{tc.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 통계 요약 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-terminal-input rounded-lg p-3 border border-terminal-border text-center">
          <p className="text-2xl font-bold text-purple-400">{features.length}</p>
          <p className="text-xs text-terminal-dim">{lang === 'ko' ? '기능 그룹' : 'Feature Groups'}</p>
        </div>
        <div className="bg-terminal-input rounded-lg p-3 border border-terminal-border text-center">
          <p className="text-2xl font-bold text-cyan-400">{connections.filter(c => c.type === 'strong').length}</p>
          <p className="text-xs text-terminal-dim">{lang === 'ko' ? '강한 연결' : 'Strong Connections'}</p>
        </div>
        <div className="bg-terminal-input rounded-lg p-3 border border-terminal-border text-center">
          <p className="text-2xl font-bold text-terminal-text">{testcases.length}</p>
          <p className="text-xs text-terminal-dim">{lang === 'ko' ? '전체 테스트' : 'Total Tests'}</p>
        </div>
        <div className="bg-terminal-input rounded-lg p-3 border border-terminal-border text-center">
          <p className="text-2xl font-bold text-status-go">
            {testcases.filter(tc => tc.test_category === 'core').length}
          </p>
          <p className="text-xs text-terminal-dim">{lang === 'ko' ? '핵심 시나리오' : 'Core Scenarios'}</p>
        </div>
      </div>
    </div>
  );
}

